import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Target, Users, ShieldCheck, ArrowRight, Compass } from 'lucide-react';

const FOUNDERS = [
  {
    name: 'Krisha Sheth',
    role: 'Co-Founder',
    initials: 'KS',
    bio: "The student whose curiosity sparked CareersPOV. Krisha brings the perspective that matters most: that of the students we're building it for.",
  },
  {
    name: 'Sachin Sheth',
    role: 'Co-Founder',
    initials: 'SS',
    bio: 'A technology and business professional passionate about helping people grow. Sachin brings experience in building solutions and turning ideas into meaningful opportunities.',
  },
];

const VALUES = [
  { icon: Heart, title: 'Parent-Driven', desc: 'The best mentors are already part of your school. Parents bring real-world experience, credibility, and a genuine intent to guide the next generation.' },
  { icon: Target, title: 'Student-First', desc: 'Students explore careers at their own pace, through real conversations and practical exposure — not pressure or assumptions.' },
  { icon: Users, title: 'Community-Powered', desc: 'No outsiders or random networks. Every mentor, coordinator, and student is part of a trusted school ecosystem.' },
  { icon: ShieldCheck, title: 'Trust by Design', desc: 'Everything is school-managed — from session approvals to participation — ensuring a safe and structured environment.' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in-up">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#F97D6A] bg-[#FFF0ED] px-3 py-1.5 rounded-full mb-6" data-testid="about-label">
            About CareersPOV
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937] font-['Outfit']" data-testid="about-heading">
            From One Question to a Shared Mission
          </h1>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-14 sm:pb-20" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-fade-in-up space-y-6 max-w-4xl mx-auto text-center text-[#4B5563] text-base sm:text-[17px] leading-[1.85]">
            <p>Every career begins with a choice. But how can students make that choice without knowing what a career is really like?</p>
            <p>At CareersPOV, we believe students deserve more than career descriptions, aptitude tests and advice. They deserve the opportunity to explore, ask questions and experience the real world before deciding their future.</p>
          </div>
        </div>
      </section>

      {/* It Started With a Question */}
      <section className="pb-14 sm:pb-20" data-testid="about-question">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-8 animate-fade-in-up text-center">
            It Started With a Question
          </h2>
          <div className="animate-fade-in-up space-y-6 max-w-4xl mx-auto text-center text-[#4B5563] text-base sm:text-[17px] leading-[1.85]">
            <p className="text-[#1F2937] text-xl sm:text-[22px] font-medium font-['Outfit'] leading-[1.5] italic py-2">
              "Can I experience different careers before I choose my subjects?"
            </p>
            <p>It was a simple question from Krisha, a student trying to understand her options.</p>
            <p>She could read about careers, watch videos and speak to people. But she wanted something more. She wanted to see what professionals actually do, understand their everyday work and discover whether a career genuinely interested her.</p>
            <p className="text-[#1F2937] font-semibold text-lg sm:text-xl font-['Outfit']">That question sparked CareersPOV.</p>
            <p>What began as a conversation between a daughter and her father has grown into a shared mission.</p>
            <p>Today, we're building CareersPOV together, bringing students, parents, professionals and schools into one trusted community where career exploration goes beyond information.</p>
          </div>
        </div>
      </section>

      {/* Why CareersPOV Exists */}
      <section className="pb-14 sm:pb-20" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-8 animate-fade-in-up text-center">
            Why CareersPOV Exists
          </h2>
          <div className="animate-fade-in-up space-y-6 max-w-4xl mx-auto text-center text-[#4B5563] text-base sm:text-[17px] leading-[1.85]">
            <p>Students are often expected to make important educational choices before they've had meaningful exposure to the careers those choices may lead to.</p>
            <p className="text-[#1F2937] font-medium text-lg">We want to change that.</p>
            <p>Through career exploration, conversations with professionals, workplace visits, internships and other real-world experiences, CareersPOV helps students discover possibilities and make more informed decisions.</p>
            <p>Our goal isn't to tell students which careers to choose.</p>
            <p className="text-[#1F2937] font-semibold text-lg sm:text-xl font-['Outfit']">It's to help them discover what's possible for themselves.</p>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="pb-14 sm:pb-20" data-testid="about-founders">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-8 animate-fade-in-up text-center">
            The People Behind CareersPOV
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {FOUNDERS.map((f, i) => (
              <div
                key={f.name}
                className={`bg-white rounded-2xl border border-gray-200 p-6 animate-fade-in-up stagger-${i + 1}`}
                data-testid={`founder-card-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[#FFF0ED] flex items-center justify-center shrink-0">
                    <span className="text-sm font-extrabold text-[#F97D6A] font-['Outfit']">{f.initials}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] leading-tight">{f.name}</h3>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mt-0.5">{f.role}</p>
                  </div>
                </div>
                <p className="text-sm text-[#4B5563] leading-relaxed">{f.bio}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-base text-[#4B5563] leading-[1.85] animate-fade-in-up max-w-4xl mx-auto text-center">
            Together, we're building something we wish every student could experience before making important career decisions.
          </p>
        </div>
      </section>

      {/* What We Believe */}
      <section className="pb-16 sm:pb-20" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-8 animate-fade-in-up text-center">
            What We Believe
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((item, i) => (
              <div
                key={item.title}
                className={`bg-white rounded-2xl border border-gray-200 p-6 animate-fade-in-up stagger-${i + 1}`}
                data-testid={`about-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#FFF0ED] flex items-center justify-center mb-3">
                  <item.icon className="h-5 w-5 text-[#F97D6A]" />
                </div>
                <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-1.5">{item.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="rounded-2xl p-8 sm:p-10 animate-fade-in-up"
            style={{ background: 'linear-gradient(135deg, #FFF5F3 0%, #FFF0ED 50%, #FDE8E4 100%)' }}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
              Help Students Discover What's Possible
            </h3>
            <p className="text-sm text-[#6B7280] mb-6 max-w-md mx-auto leading-relaxed">
              Bring meaningful, real-world career experiences to your school.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/school-registration">
                <Button className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold px-6 py-3 h-auto" data-testid="about-cta-register-school">
                  Register School <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button variant="outline" className="rounded-xl text-sm font-semibold px-6 py-3 h-auto border-gray-200 text-[#1F2937]" data-testid="about-cta-explore-careers">
                  <Compass className="mr-2 h-4 w-4" /> Explore Careers
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
